// Artifact metadata — pure string parsing and transformation for Contract v2 artifact documents
// (docs/ARTIFACTS_PLAN.md §4, §6). An artifact is ONE complete self-contained HTML document (a 1920x1080
// slide). Under Contract v2 it declares itself: a JSON manifest script in <head>, a :root block of --a-*
// tokens, data-step choreography. This module reads those declared surfaces and performs the one
// deterministic write the theming layer needs — swapping token VALUES in place, no model call.
//
// Pure by contract: string in, string/data out. No DOM APIs (runs in browser and node), never throws —
// a malformed document degrades to null / unchanged output, never to an exception. Regexes stay linear
// (bounded character classes, indexOf slicing) so a hostile document cannot hang the caller either.

/** The manifest an artifact exports from its `<script type="application/json" id="artifact-manifest">`
 *  block. Only `contract` is guaranteed by the parser; everything else is passed through as authored. */
export interface ArtifactManifest {
  contract: number
  title?: string
  aesthetic?: string
  theme?: 'deck' | 'own'
  /** Deck runtime version this document assumes (deckRuntime.ts DECK_RUNTIME_VERSION). */
  runtime?: number
  sequenceMs?: number
  steps?: number
  /** The ONE structure the document is built on — a `LayoutPattern` word as the author wrote it; readers
   *  normalise with `asLayoutPattern`, never trust it raw. */
  composition?: string
  sources?: unknown[]
}

/** Opening tag of the manifest script, any attribute order, quoted or bare id value. */
const MANIFEST_OPEN_RE =
  /<script\b[^>]*\bid\s*=\s*(?:"artifact-manifest"|'artifact-manifest'|artifact-manifest(?=[\s>/]))[^>]*>/i

/**
 * Extracts and parses the artifact manifest. Tolerates attribute order and whitespace in the script tag.
 * Returns null when the block is absent, its JSON does not parse, the JSON is not an object, or
 * `contract` is not a finite number. Nothing else is validated or coerced — the rest of the manifest is
 * the author's word, taken as written.
 */
export function parseArtifactManifest(html: string): ArtifactManifest | null {
  try {
    if (typeof html !== 'string' || html === '') return null
    const open = MANIFEST_OPEN_RE.exec(html)
    if (!open) return null
    const start = open.index + open[0].length
    const rest = html.slice(start)
    const endRel = rest.search(/<\/script/i)
    if (endRel < 0) return null
    const parsed: unknown = JSON.parse(rest.slice(0, endRel).trim())
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    const contract = (parsed as { contract?: unknown }).contract
    if (typeof contract !== 'number' || !Number.isFinite(contract)) return null
    return parsed as ArtifactManifest
  } catch {
    return null
  }
}

/** The first `:root` block: `body` is the text between its braces, `start`/`end` are the indices of that
 *  span in the document (start = after `{`, end = at `}`), so a caller can splice a rewritten body back. */
function firstRootBlock(html: string): { start: number; end: number; body: string } | null {
  const rootIdx = html.indexOf(':root')
  if (rootIdx < 0) return null
  const open = html.indexOf('{', rootIdx)
  if (open < 0) return null
  const close = html.indexOf('}', open + 1)
  if (close < 0) return null
  return { start: open + 1, end: close, body: html.slice(open + 1, close) }
}

/** One `--a-*` declaration: name, the `:` separator with its whitespace, and the value (no `;`). */
const TOKEN_DECL_RE = /(--a-[A-Za-z0-9_-]*)(\s*:\s*)([^;}]*)/g

/**
 * Reads every `--a-*` custom property out of the FIRST `:root` block in the document. Values come back
 * trimmed, without the trailing semicolon. Returns null when the document has no `:root` block; an empty
 * record when the block exists but declares no `--a-*` tokens.
 */
export function parseTokens(html: string): Record<string, string> | null {
  try {
    if (typeof html !== 'string') return null
    const block = firstRootBlock(html)
    if (!block) return null
    const tokens: Record<string, string> = {}
    const re = new RegExp(TOKEN_DECL_RE.source, 'g')
    let m: RegExpExecArray | null
    while ((m = re.exec(block.body)) !== null) {
      tokens[m[1]] = m[3].trim()
    }
    return tokens
  } catch {
    return null
  }
}

/**
 * The deterministic re-theme primitive (plan §6, Layer A): replaces the VALUES of matching `--a-*`
 * properties inside the first `:root` block only. Everything else — formatting inside the block, the
 * whole rest of the document — is preserved byte-for-byte. Keys in `overrides` are full property names
 * (`'--a-bg'`). Overrides for properties the block does not declare are ignored (nothing is inserted).
 * Returns the input unchanged when there is no `:root` block or nothing matches.
 */
export function swapTokens(html: string, overrides: Record<string, string>): string {
  try {
    if (typeof html !== 'string') return html
    if (!overrides || typeof overrides !== 'object') return html
    const block = firstRootBlock(html)
    if (!block) return html
    const re = new RegExp(TOKEN_DECL_RE.source, 'g')
    const nextBody = block.body.replace(re, (full, name: string, sep: string, value: string) => {
      if (!Object.prototype.hasOwnProperty.call(overrides, name)) return full
      const override = overrides[name]
      if (typeof override !== 'string') return full
      // Keep whatever whitespace trailed the old value (e.g. before a `}` on the same line).
      const trail = /\s*$/.exec(value)?.[0] ?? ''
      return name + sep + override.trim() + trail
    })
    if (nextBody === block.body) return html
    return html.slice(0, block.start) + nextBody + html.slice(block.end)
  } catch {
    return html
  }
}

/**
 * The href of every Google Fonts stylesheet `<link>` in the document, deduplicated in document order.
 * Only `https://fonts.googleapis.com/...` hrefs count, which naturally excludes bare-origin preconnect
 * links. Attribute order inside the tag does not matter.
 */
export function extractFontHrefs(html: string): string[] {
  const out: string[] = []
  try {
    if (typeof html !== 'string') return out
    const linkRe = /<link\b[^>]*>/gi
    let m: RegExpExecArray | null
    while ((m = linkRe.exec(html)) !== null) {
      const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(m[0])
      const value = href ? (href[1] ?? href[2] ?? '') : ''
      if (value.startsWith('https://fonts.googleapis.com/') && !out.includes(value)) out.push(value)
    }
  } catch {
    // tolerate — return whatever was collected
  }
  return out
}

/** The handful of entities that show up in headline text, decoded without a DOM. */
function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
}

/**
 * One line that names the artifact, used to describe sibling slides to the model: the manifest title when
 * present, else the text of the first h1/h2 stripped of tags and truncated to 80 characters, else null.
 */
export function artifactOneLiner(html: string): string | null {
  try {
    if (typeof html !== 'string') return null
    const manifest = parseArtifactManifest(html)
    if (manifest && typeof manifest.title === 'string' && manifest.title.trim() !== '') {
      return manifest.title.trim()
    }
    const heading = /<h([12])\b[^>]*>([\s\S]*?)<\/h\1\s*>/i.exec(html)
    if (!heading) return null
    const text = decodeEntities(heading[2].replace(/<[^>]*>/g, ' '))
      .replace(/\s+/g, ' ')
      .trim()
    if (text === '') return null
    return text.length > 80 ? text.slice(0, 79).trimEnd() + '…' : text
  } catch {
    return null
  }
}
